import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Request DTO für KI-API
 */
export interface AiRequest {
  promptName: string;
  parameters: { [key: string]: string };
  calculationId?: number;
  cacheFieldName?: string;
}

/**
 * Response DTO für KI-API
 */
export interface AiResponse {
  success: boolean;
  answer: string;
  error?: string;
  timestamp: string;
  additionalData?: Record<string, any>;
}

/**
 * Service für Google Gemini KI-Integration
 * Kommuniziert mit dem Backend /api/ai/ask Endpoint
 */
@Injectable({
  providedIn: 'root'
})
export class AiService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/ai`; // Backend URL

  /**
   * Sendet eine KI-Anfrage an das Backend
   * @param request AiRequest mit PromptName und Parametern
   * @returns Observable mit AiResponse (Format: "ZAHL|BEGRÜNDUNG")
   */
  askAi(request: AiRequest): Observable<AiResponse> {
    return this.httpClient.post<AiResponse>(`${this.apiUrl}/ask`, request);
  }

  /**
   * Parst die KI-Antwort im Format "ZAHL [EUR]|BEGRÜNDUNG"
   * Unterstützt optionales EUR-Suffix für Nettokaltmiete
   * Robust gegen Formatvariationen
   * @param answer Antwort der KI (z.B. "14.50 EUR|Berlin zentral" oder "2.50|Wachstum")
   * @returns { value: "14.50", reason: "Berlin zentral" }
   */
  parseAiAnswer(answer: string): { value: string; reason: string } {
    // Regex: (\d+[.,]\d+)(?:\s+EUR)?\|(.+)
    // - (\d+[.,]\d+) = Dezimalzahl mit Komma oder Punkt (Capturing Group 1)
    // - (?:\s+EUR)? = Optional: Space + EUR (Non-Capturing Group, nicht erfasst)
    // - \| = Pipe-Zeichen als Trenner
    // - (.+) = Rest bis zum Ende (Begründung, Capturing Group 2)
    const regexMatch = answer.match(/(\d+[.,]\d+)(?:\s+EUR)?\|(.+)/);
    
    if (regexMatch && regexMatch[1] && regexMatch[2]) {
      return {
        value: regexMatch[1].trim(),
        reason: regexMatch[2].trim()
      };
    }
    
    // Fallback auf einfaches Split, falls Regex nicht passt
    const parts = answer.split('|');
    return {
      value: parts[0]?.trim() || '',
      reason: parts[1]?.trim() || ''
    };
  }

  /**
   * Konvertiert die Wertzahl zu einem numerischen Wert
   * @param value String-Wert von der KI (z.B. "14.50")
   * @returns number oder null bei Fehler
   */
  parseValue(value: string): number | null {
    try {
      const num = parseFloat(value.replace(',', '.'));
      return isNaN(num) ? null : num;
    } catch {
      return null;
    }
  }
}
